import { useLazyQuery } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '~components/input/input';
import Loading from '~components/loading';
import { useRegisterReleaseHintReference } from '~containers/release-hint/use-register-release-hint-reference';
import { BasicClose, BasicSearch } from '~svg-components';
import { getGlobalSearchResultLocalStorage } from '~utils/recently-viewed-storage';
import useDisplayResponseErrorToast from '~utils/use-display-response-error-toast';
import { useDebounce } from '~utils/use-effect';
import {
  QUERY_GET_GLOBAL_SEARCH_BILLING,
  QUERY_GET_GLOBAL_SEARCH_BOOKING_AND_STUDENT,
  QUERY_GET_GLOBAL_SEARCH_NOMINATION_PLAN,
} from './gql';
import RecentlyViewedPanel from './recently-viewed-panel';
import SearchPanel from './search-panel';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
      marginLeft: '16px',
    },
    screen: {
      position: 'fixed',
      backgroundColor: 'transparent',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      zIndex: '999',
    },
    input: {
      position: 'relative',
      height: '32px',
      padding: '10px 12px',
      border: 0,
      backgroundColor: theme.colors.white,
      borderRadius: '16px',
      cursor: 'pointer',
      opacity: 0.8,
      zIndex: '999',
      outline: 'none',
      boxShadow: 'none !important',
      '&:hover': {
        opacity: 1,
        backgroundColor: `${theme.colors.white} !important`,
      },
      '&:hover > input': {
        opacity: 1,
        backgroundColor: `${theme.colors.white} !important`,
      },
      '&:focus': {
        outline: 'none',
      },
      '& > input': {
        cursor: 'pointer',
      },
      transition: '0.3s',
    },

    searchFeatureBeUsed: {
      opacity: 1,
      backgroundColor: `${theme.colors.white} !important`,
      transition: '0.3s',
    },

    searchBarBlackFeatureBeUsed: {
      opacity: 1,
      backgroundColor: `${theme.colors.greyF7} !important`,
      transition: '0.3s',
    },

    basicCloseIcon: {
      position: 'absolute',
      padding: '4px 13px',
      right: 0,
    },

    globalSearchBarBlack: {
      backgroundColor: theme.colors.greyF7,
      '&:hover': {
        backgroundColor: `${theme.colors.greyF7} !important`,
      },
      '&:hover > input': {
        backgroundColor: `${theme.colors.greyF7} !important`,
      },
    },

    loadingPage: {
      ...theme.flex.center,
      position: 'absolute',
      width: 616,
      height: 396,
      zIndex: '999',
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadow.componentShallow,
      borderRadius: theme.borderRadius.big,
      border: `1px solid ${theme.colors.greyE8}`,
    },
  }),
  { name: 'GlobalSearchBar' }
);

function GlobalSearchBar({ isSticky, focusGlobalSearch = () => {} }) {
  const registerReleaseHintReference = useRegisterReleaseHintReference();
  const { t } = useTranslation('global-search');
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [globalSearchBookingAndStudentResult, setGlobalSearchBookingAndStudentResult] = useState(
    null
  );
  const [globalSearchBillingResult, setGlobalSearchBillingResult] = useState(null);
  const [globalSearchNominationPlan, setGlobalSearchNominationPlanResult] = useState(null);
  const useDebounceInputValue = useDebounce(inputValue);
  const displayResponseErrorToast = useDisplayResponseErrorToast();

  const [showRecentlyViewed, setShowRecentlyRecentlyViewed] = useState(null);
  const [recentlyViewedStudent, setRecentlyViewedStudent] = useState(null);
  const [recentlyViewedBooking, setRecentlyViewedBooking] = useState(null);
  const [recentlyViewedNominationPlan, setRecentlyViewedNominationPlan] = useState(null);

  const [clickInput, setClickInput] = useState(false);

  const searchPanelRef = useRef(null);

  const [
    queryGetGlobalSearchBookingAndStudent,
    queryGetGlobalSearchBookingAndStudentResult,
  ] = useLazyQuery(QUERY_GET_GLOBAL_SEARCH_BOOKING_AND_STUDENT, {
    onCompleted: ({ getGlobalSearchBookingAndStudent }) => {
      setGlobalSearchBookingAndStudentResult(getGlobalSearchBookingAndStudent);
    },
    onError: (error) => {
      displayResponseErrorToast(error);
    },
  });

  const [queryGetGlobalSearchBilling, queryGetGlobalSearchBillingResult] = useLazyQuery(
    QUERY_GET_GLOBAL_SEARCH_BILLING,
    {
      onCompleted: ({ getGlobalSearchBilling }) => {
        setGlobalSearchBillingResult(getGlobalSearchBilling);
      },
      onError: (error) => {
        displayResponseErrorToast(error);
      },
    }
  );

  const [
    queryGetGlobalSearchNominationPlan,
    queryGetGlobalSearchNominationPlanResult,
  ] = useLazyQuery(QUERY_GET_GLOBAL_SEARCH_NOMINATION_PLAN, {
    onCompleted: ({ getGlobalSearchNominationPlan }) => {
      setGlobalSearchNominationPlanResult(getGlobalSearchNominationPlan);
    },
    onError: (error) => {
      displayResponseErrorToast(error);
    },
  });

  const getRecentlyViewedSearchFromLocalStorage = useCallback(() => {
    const localStorageRecentlyViewedRes = JSON.parse(getGlobalSearchResultLocalStorage());
    setShowRecentlyRecentlyViewed(!!localStorageRecentlyViewedRes);
    if (localStorageRecentlyViewedRes) {
      setRecentlyViewedStudent(localStorageRecentlyViewedRes.students);
      setRecentlyViewedBooking(localStorageRecentlyViewedRes.bookings);
      setRecentlyViewedNominationPlan(localStorageRecentlyViewedRes.nominationPlans);
    }
  }, []);

  const clearAllTabSearchResult = () => {
    setGlobalSearchBookingAndStudentResult(null);
    setGlobalSearchBillingResult(null);
    setGlobalSearchNominationPlanResult(null);
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    if (value) {
      setShowRecentlyRecentlyViewed(false);
    }
    if (!value) {
      clearAllTabSearchResult();
      getRecentlyViewedSearchFromLocalStorage();
    }
  };

  const clearSearch = useCallback(() => {
    setInputValue('');
    clearAllTabSearchResult();
  }, []);

  const handleClickSearchStudentAndBooking = () => {
    queryGetGlobalSearchBookingAndStudent({
      variables: {
        searchValue: useDebounceInputValue.trim(),
      },
    });
  };

  const handleClickSearchBilling = () => {
    queryGetGlobalSearchBilling({
      variables: {
        searchValue: useDebounceInputValue.trim(),
      },
    });
  };

  const handleClickSearchNominationPlan = () => {
    queryGetGlobalSearchNominationPlan({
      variables: {
        searchValue: useDebounceInputValue.trim(),
      },
    });
  };

  useEffect(() => {
    if (useDebounceInputValue) {
      if (useDebounceInputValue.trim()) {
        if (searchPanelRef.current?.currentTab === 'group_plan') {
          queryGetGlobalSearchNominationPlan({
            variables: {
              searchValue: useDebounceInputValue.trim(),
            },
          });
        } else if (searchPanelRef.current?.currentTab === 'transaction') {
          queryGetGlobalSearchBilling({
            variables: {
              searchValue: useDebounceInputValue.trim(),
            },
          });
        } else {
          queryGetGlobalSearchBookingAndStudent({
            variables: {
              searchValue: useDebounceInputValue.trim(),
            },
          });
        }
      } else {
        clearSearch();
      }
    }
    if (!useDebounceInputValue) {
      clearSearch();
    }
  }, [
    clearSearch,
    queryGetGlobalSearchBilling,
    queryGetGlobalSearchBookingAndStudent,
    queryGetGlobalSearchNominationPlan,
    useDebounceInputValue,
  ]);

  const handleInputClick = () => {
    setClickInput(true);
    document.body.style.overflow = 'hidden';
    if (!useDebounceInputValue) getRecentlyViewedSearchFromLocalStorage();
  };

  const initGlobalSearch = (isHoldFocusedGlobalSearchBar = false) => {
    setInputValue('');
    clearAllTabSearchResult();
    setShowRecentlyRecentlyViewed(isHoldFocusedGlobalSearchBar);
    setClickInput(isHoldFocusedGlobalSearchBar);
  };

  const handleClickCloseSearchResult = () => {
    document.body.style.overflow = 'initial';
    initGlobalSearch();
  };

  const handleClickClearSearchInputValue = () => {
    initGlobalSearch(true);
    document.getElementById('global-search-bar-input').focus();
  };

  const renderStartIcon = () => <BasicSearch color="grey99" fontSize={14} />;

  const renderEndIcon = () => {
    if (inputValue) {
      return (
        <div className={classes.basicCloseIcon} onClick={handleClickClearSearchInputValue}>
          <BasicClose color="grey66" fontSize={10} />
        </div>
      );
    }
  };

  const renderInitLoading = () => (
    <div className={classes.loadingPage}>
      <Loading />
    </div>
  );

  const searchFeatureBeUsed =
    showRecentlyViewed ||
    inputValue ||
    (!queryGetGlobalSearchBookingAndStudentResult.loading &&
      !queryGetGlobalSearchBillingResult.loading &&
      !queryGetGlobalSearchNominationPlanResult.loading &&
      globalSearchBookingAndStudentResult) ||
    clickInput;

  useEffect(() => {
    if (searchFeatureBeUsed) {
      focusGlobalSearch('focus');
    } else {
      focusGlobalSearch('close');
      clearAllTabSearchResult();
    }
  }, [focusGlobalSearch, searchFeatureBeUsed]);

  return (
    <div className={classes.root}>
      {searchFeatureBeUsed && (
        <div className={classes.screen} onClick={handleClickCloseSearchResult} />
      )}
      <Input
        id="global-search-bar-input"
        style={{ width: searchFeatureBeUsed ? '400px' : '140px' }}
        value={inputValue}
        onClick={handleInputClick}
        onChange={handleInputChange}
        autoComplete="off"
        endAdornment={renderEndIcon()}
        startAdornment={renderStartIcon()}
        placeholder={searchFeatureBeUsed ? t('search_name_email_id_bed_reference_info') : ''}
        className={classnames(classes.input, {
          [classes.globalSearchBarBlack]: isSticky,
          [classes.searchBarBlackFeatureBeUsed]: isSticky && searchFeatureBeUsed,
          [classes.searchFeatureBeUsed]: searchFeatureBeUsed,
        })}
        ref={registerReleaseHintReference('2021-02-20', 'global-search-bar')}
      />
      {(globalSearchBookingAndStudentResult ||
        globalSearchBillingResult ||
        globalSearchNominationPlan) && (
        <SearchPanel
          ref={searchPanelRef}
          globalSearchBookingAndStudentResult={globalSearchBookingAndStudentResult}
          globalSearchBillingResult={globalSearchBillingResult}
          globalSearchNominationPlan={globalSearchNominationPlan}
          handleClickSearchStudentAndBooking={handleClickSearchStudentAndBooking}
          handleClickSearchBilling={handleClickSearchBilling}
          handleClickSearchNominationPlan={handleClickSearchNominationPlan}
          loading={
            queryGetGlobalSearchBookingAndStudentResult.loading ||
            queryGetGlobalSearchBillingResult.loading ||
            queryGetGlobalSearchNominationPlanResult.loading
          }
        />
      )}
      {!globalSearchBookingAndStudentResult &&
        queryGetGlobalSearchBookingAndStudentResult.loading &&
        renderInitLoading()}
      {showRecentlyViewed && (
        <RecentlyViewedPanel
          students={recentlyViewedStudent}
          bookings={recentlyViewedBooking}
          nominationPlans={recentlyViewedNominationPlan}
        />
      )}
    </div>
  );
}

GlobalSearchBar.propTypes = {
  isSticky: PropTypes.bool.isRequired,
  focusGlobalSearch: PropTypes.func.isRequired,
};

export default GlobalSearchBar;
